import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { generateBookingFromReceipt } from '../apis/bookingApi';

/**
 * Hook to trigger booking generation for a receipt in ready status.
 * On success, invalidates the receipt cache so polling picks up the new state.
 */
export const useGenerateBooking = (chatId: string | undefined) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (receiptId: string) => {
      const token = await getAccessTokenSilently();
      return generateBookingFromReceipt(receiptId, token);
    },
    onSuccess: (_job, receiptId) => {
      queryClient.invalidateQueries({
        queryKey: ['uploaded-receipt', receiptId],
      });
      if (chatId) {
        queryClient.invalidateQueries({ queryKey: ['messages', chatId] });
      }
    },
  });

  const generateBooking = useCallback(
    async (receiptId: string): Promise<boolean> => {
      try {
        await mutation.mutateAsync(receiptId);
        return true;
      } catch {
        return false;
      }
    },
    [mutation]
  );

  return {
    generateBooking,
    isGenerating: mutation.isPending,
  };
};
